// src/components/Deposit.js – VERSION SIMPLIFIÉE (une seule étape)
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const BINANCE_AFFILIATE_LINK = 'https://www.binance.com/activity/referral-entry/CPA?ref=CPA_003YEW6C42';
const WALLET_OPTIONS = ['Binance', 'Trust Wallet', 'MetaMask', 'Coinbase'];

const Deposit = () => {
    const [selectedWallet, setSelectedWallet] = useState('');
    const [amount, setAmount] = useState('');
    const [depositAddress, setDepositAddress] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showPopup, setShowPopup] = useState(false);
    const [txId, setTxId] = useState('');
    const [verifyLoading, setVerifyLoading] = useState(false);

    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    if (!token) navigate('/');

    // ========== GÉNÉRER UNE ADRESSE ==========
    const onGenerateAddress = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });
        setDepositAddress('');

        if (!selectedWallet) {
            setMessage({ type: 'error', text: 'Sélectionnez votre portefeuille.' });
            setLoading(false);
            return;
        }

        const depositAmount = parseFloat(amount);
        if (isNaN(depositAmount) || depositAmount < 10) {
            setMessage({ type: 'error', text: 'Minimum 10 USDT.' });
            setLoading(false);
            return;
        }

        try {
            const res = await axios.post(
                'https://api.blackroq.pro/api/deposit/address',
                { amount: depositAmount },
                { headers: { 'x-auth-token': token } }
            );

            if (res.data.success && res.data.address) {
                setDepositAddress(res.data.address);
                setMessage({ 
                    type: 'success', 
                    text: `Adresse générée ! Envoyez exactement ${depositAmount} USDT (TRC20).` 
                });
            } else {
                setMessage({ type: 'error', text: 'Erreur lors de la génération.' });
            }
        } catch (err) {
            setMessage({ 
                type: 'error', 
                text: err.response?.data?.message || 'Erreur serveur.' 
            });
        } finally {
            setLoading(false);
        }
    };

    // ========== VÉRIFIER TXID (NOUVELLE VERSION - UNE SEULE ÉTAPE) ==========
    const verifyDeposit = async () => {
        if (!txId.trim()) {
            setMessage({ type: 'error', text: 'Veuillez entrer le TxID.' });
            return;
        }

        if (!depositAddress) {
            setMessage({ type: 'error', text: 'Adresse de dépôt manquante.' });
            return;
        }

        setVerifyLoading(true);
        try {
            // ⚠️ IMPORTANT : Nouvelle route avec txId ET address
            const res = await axios.post(
                'https://api.blackroq.pro/api/deposit/verify',
                { 
                    txId: txId.trim(),
                    address: depositAddress 
                },
                { headers: { 'x-auth-token': token } }
            );

            if (res.data.success) {
                setMessage({ 
                    type: 'success', 
                    text: `✅ ${res.data.amount} USDT crédités ! Nouveau solde: ${res.data.newBalance} USDT` 
                });
                
                // Réinitialiser le formulaire
                setTxId('');
                setShowPopup(false);
                
                // Rediriger ou rafraîchir après 3 secondes
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 
                            err.response?.data?.error || 
                            'Erreur de vérification';
            
            setMessage({ 
                type: 'error', 
                text: `❌ ${errorMsg}` 
            });
        } finally {
            setVerifyLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(depositAddress)
            .then(() => {
                setMessage({ type: 'success', text: 'Adresse copiée !' });
                setTimeout(() => setMessage({ type: '', text: '' }), 2000);
            })
            .catch(() => setMessage({ type: 'error', text: 'Erreur de copie' }));
    };

    const openVerifyPopup = () => setShowPopup(true);
    const closeVerifyPopup = () => {
        setShowPopup(false);
        setTxId('');
    };

    const renderInitialPrompt = () => (
        <div className="text-center mt-5">
            <h2 className="mb-4">D'où souhaitez-vous déposer l'USDT ?</h2>
            <div className="d-grid gap-3 col-md-6 mx-auto mt-4">
                {WALLET_OPTIONS.map(w => (
                    <button key={w} className="btn btn-primary btn-lg" onClick={() => setSelectedWallet(w)}>
                        Déposer depuis {w}
                    </button>
                ))}
            </div>
            <button className="btn btn-outline-secondary btn-sm mt-4" onClick={() => window.open(BINANCE_AFFILIATE_LINK, '_blank')}>
                Créer un compte Binance
            </button>
        </div>
    );

    const renderDepositInstructions = () => (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6">
                    <h1 className="text-center mb-4">Dépôt depuis {selectedWallet}</h1>

                    {message.text && (
                        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} text-center`}>
                            {message.text}
                        </div>
                    )}

                    <div className="card shadow-sm p-4 mb-4">
                        <form onSubmit={onGenerateAddress}>
                            <div className="form-group mb-3">
                                <label>Montant en USDT (minimum 10)</label>
                                <input 
                                    type="number" 
                                    className="form-control" 
                                    value={amount} 
                                    onChange={e => setAmount(e.target.value)} 
                                    required 
                                    min="10" 
                                    step="0.01"
                                />
                            </div>
                            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                                {loading ? 'Génération...' : 'Générer une adresse de dépôt'}
                            </button>
                        </form>
                    </div>

                    {depositAddress && (
                        <div className="card text-center bg-light p-4 shadow-lg">
                            <h3 className="text-primary mb-3">Adresse de Dépôt USDT (TRC20)</h3>
                            <div className="mb-3 mx-auto p-2 border rounded bg-white">
                                <QRCodeSVG value={depositAddress} size={180} level="H" />
                            </div>
                            <div className="d-flex justify-content-between align-items-center bg-white p-3 border rounded mb-3">
                                <code className="small text-break">{depositAddress}</code>
                                <button className="btn btn-sm btn-outline-success ms-3" onClick={copyToClipboard}>
                                    Copier
                                </button>
                            </div>
                            <p className="small text-danger fw-bold">
                                ⚠️ Envoyez EXACTEMENT {amount} USDT sur le réseau TRC20.
                            </p>

                            <button className="btn btn-success w-100 mt-3" onClick={openVerifyPopup}>
                                ✅ J'ai envoyé les USDT → Vérifier avec TxID
                            </button>
                        </div>
                    )}

                    <div className="text-center mt-3">
                        <button 
                            className="btn btn-link" 
                            onClick={() => { 
                                setDepositAddress(''); 
                                setAmount(''); 
                                setSelectedWallet(''); 
                                setMessage({ type: '', text: '' }); 
                                setTxId(''); 
                            }}
                        >
                            ← Revenir au choix du portefeuille
                        </button>
                    </div>
                </div>
            </div>

            {/* POPUP DE VÉRIFICATION TXID */}
            {showPopup && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Vérification du dépôt</h5>
                                <button type="button" className="btn-close" onClick={closeVerifyPopup}></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Avez-vous bien envoyé <strong>{amount} USDT</strong> à l'adresse :
                                </p>
                                <code className="d-block p-2 bg-light text-break mb-3">
                                    {depositAddress}
                                </code>
                                
                                <div className="alert alert-warning mb-3">
                                    <strong>⚠️ Important :</strong> 
                                    <ul className="mb-0 mt-2">
                                        <li>Le montant doit être exactement <strong>{amount} USDT</strong></li>
                                        <li>Le réseau doit être <strong>TRC20 (TRON)</strong></li>
                                        <li>Le TxID est unique et ne peut être utilisé qu'une fois</li>
                                    </ul>
                                </div>

                                <div className="form-group">
                                    <label>Collez votre TxID (Hash de transaction) :</label>
                                    <input
                                        type="text"
                                        className="form-control mb-3"
                                        placeholder="Ex: abc123... ou 0xabc123..."
                                        value={txId}
                                        onChange={(e) => setTxId(e.target.value)}
                                    />
                                    <small className="text-muted">
                                        Trouvez le TxID dans l'historique de transactions de {selectedWallet}
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={closeVerifyPopup}>
                                    Annuler
                                </button>
                                <button 
                                    className="btn btn-success" 
                                    onClick={verifyDeposit}
                                    disabled={verifyLoading || !txId.trim()}
                                >
                                    {verifyLoading ? 'Vérification en cours...' : 'Vérifier et créditer'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return <div className="container">{selectedWallet ? renderDepositInstructions() : renderInitialPrompt()}</div>;
};

export default Deposit;